import React from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { usePlanURL } from "~/components/plans/hooks/UsePlanURL";
import { useNavigate } from "react-router";
import { CloseDialogButton } from "~/components/plans/components/CloseDialogButton";
import { ButterLogo } from "~/components/ButterLogo";
import { useIOSScrollLock } from "~/hooks/use-ios-scroll-lock";

type PropTypes = {
  open?: boolean;
  onClose?: () => void;
  includeLogo?: boolean;
  fullScreenOnMobile?: boolean;
  children: React.ReactNode;
};

export const PlanDialog = ({
  includeLogo = true,
  open = true,
  onClose,
  fullScreenOnMobile = false,
  children,
}: PropTypes) => {
  const navigate = useNavigate();
  const planURL = usePlanURL();

  useIOSScrollLock(open);

  const wrapperPadding = fullScreenOnMobile ? "p-0 sm:p-4" : "p-4";
  const panelLayout = fullScreenOnMobile
    ? "items-stretch sm:items-center"
    : "items-center";
  const panelShape = fullScreenOnMobile
    ? "flex h-dvh flex-col overflow-y-auto rounded-none sm:my-8 sm:block sm:h-auto sm:overflow-visible sm:rounded-[20px]"
    : "overflow-visible rounded-[20px] sm:my-8";

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (onClose) {
          onClose();
        } else {
          navigate(planURL, { replace: true });
        }
      }}
      transition
      className={`fixed inset-0 z-50 flex w-screen items-center justify-center bg-butter-green/05 transition duration-300 ease-out data-closed:opacity-0 ${wrapperPadding}`}
    >
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto overscroll-contain">
        <div className={`flex min-h-full justify-center ${panelLayout} ${wrapperPadding}`}>
          <DialogPanel
            className={`relative transform bg-butter-cream-light px-6 pt-6 pb-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in w-full sm:max-w-lg sm:p-6 sm:data-closed:translate-y-0 sm:data-closed:scale-95 ${panelShape}`}
          >
            {includeLogo && <ButterLogo />}
            <CloseDialogButton onClose={onClose} />
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
